import React, { useState } from 'react';
import { X, Zap, GitBranch, Shield, Tag, Inbox, BellRing, TrendingDown, AlertTriangle, UserCheck, Clock, FileText, TrendingUp, PieChart, BarChart3, Users } from 'lucide-react';
import './WorkspaceSuggestionsModal.css';

const WORKSPACE_INSIGHTS = [
  {
    id: 'ws-1',
    title: 'Revenue metric mismatch across teams',
    description: 'Your reports use gross_revenue (price × quantity), but 3 other workspaces in your org calculate net_revenue (after discounts and returns). This may cause conflicting numbers in leadership reviews.',
    impact: 'high',
    source: 'Cross-workspace metric comparison',
    icon: <Zap size={18} />,
  },
  {
    id: 'ws-2',
    title: 'Customer churn definition differs',
    description: 'You define churned customers as "no purchase in 90 days", but the Sales team workspace uses "no login in 60 days". Aligning on one definition would make cross-team reporting consistent.',
    impact: 'high',
    source: 'Cross-workspace concept comparison',
    icon: <Shield size={18} />,
  },
  {
    id: 'ws-3',
    title: 'Missing discount impact in margin analysis',
    description: 'Your margin reports don\'t account for the "discount_code" field. The Finance team workspace factors in discount codes and sees 8-12% lower margins on promoted orders.',
    impact: 'medium',
    source: 'Cross-workspace field usage',
    icon: <Tag size={18} />,
  },
  {
    id: 'ws-4',
    title: 'Product category grouping differs from ops',
    description: 'You group products into 5 categories, but the Operations workspace uses 8 categories with a separate "Accessories" and "Bundles" split. This affects inventory and sales comparisons.',
    impact: 'medium',
    source: 'Cross-workspace classification comparison',
    icon: <GitBranch size={18} />,
  },
];

const AUTOMATION_SUGGESTIONS = [
  {
    id: 'auto-1',
    title: 'Revenue drop alert',
    description: 'You\'ve run revenue analysis 8 times this month. Want me to monitor daily revenue and alert you if it drops more than 15% compared to the 7-day rolling average?',
    trigger: 'Daily at 9:00 AM',
    basedOn: 'Your "Monthly Revenue Summary" report and 3 related scripts',
    icon: <TrendingDown size={18} />,
  },
  {
    id: 'auto-2',
    title: 'New customer spike notification',
    description: 'I noticed you frequently check customer acquisition numbers. I can notify you whenever daily sign-ups exceed 2 standard deviations above your monthly average.',
    trigger: 'Real-time, checked hourly',
    basedOn: 'Your recent queries on the customers and signups tables',
    icon: <UserCheck size={18} />,
  },
  {
    id: 'auto-3',
    title: 'Inventory reorder warning',
    description: 'Your inventory turnover scripts flag items below safety stock. Want me to run this check nightly and send you a summary of any products that need reordering?',
    trigger: 'Nightly at 11:00 PM',
    basedOn: 'Script "inventory_turnover_analysis.py" and concepts in constraints.md',
    icon: <AlertTriangle size={18} />,
  },
  {
    id: 'auto-4',
    title: 'Weekly data quality digest',
    description: 'Your workspace has 4 data validation rules in constraints.md. I can run all of them weekly and send you a digest of any violations — NULL rates, outliers, or broken foreign keys.',
    trigger: 'Every Monday at 8:00 AM',
    basedOn: 'All rules defined in concepts/constraints.md',
    icon: <Clock size={18} />,
  },
];

const SUGGESTED_REPORTS = [
  {
    id: 'sr-1',
    name: 'Monthly Revenue Summary',
    category: 'Finance',
    description: 'Generates a month-by-month revenue breakdown with trend analysis, including comparisons to prior periods and growth rates.',
    complexity: 'Balanced',
    outputFormats: ['CSV', 'Markdown'],
    icon: <TrendingUp size={18} />,
  },
  {
    id: 'sr-2',
    name: 'Customer Acquisition Funnel',
    category: 'Marketing',
    description: 'Tracks leads through each stage of the acquisition funnel, calculating conversion rates and drop-off points.',
    complexity: 'Comprehensive',
    outputFormats: ['CSV', 'HTML', 'Chart'],
    icon: <Users size={18} />,
  },
  {
    id: 'sr-3',
    name: 'Inventory Turnover Analysis',
    category: 'Operations',
    description: 'Calculates inventory turnover ratios by product category, highlighting slow-moving stock and reorder recommendations.',
    complexity: 'Balanced',
    outputFormats: ['CSV', 'Markdown'],
    icon: <PieChart size={18} />,
  },
  {
    id: 'sr-4',
    name: 'Employee Productivity Dashboard',
    category: 'HR',
    description: 'Aggregates key productivity metrics per team and individual, including task completion rates and time allocation.',
    complexity: 'Comprehensive',
    outputFormats: ['CSV', 'HTML', 'Chart'],
    icon: <BarChart3 size={18} />,
  },
  {
    id: 'sr-5',
    name: 'Top Customers by Lifetime Value',
    category: 'Sales',
    description: 'Ranks customers by total lifetime value with segmentation by acquisition channel and purchase frequency.',
    complexity: 'Simple',
    outputFormats: ['CSV', 'Markdown'],
    icon: <FileText size={18} />,
  },
];

function WorkspaceSuggestionsModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('alerts');
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [addedReportIds, setAddedReportIds] = useState(new Set());

  if (!isOpen) return null;

  const handleDismiss = (id) => {
    setDismissedIds((prev) => new Set([...prev, id]));
  };

  const handleAddReport = (id) => {
    setAddedReportIds((prev) => new Set([...prev, id]));
  };

  const visibleInsights = WORKSPACE_INSIGHTS.filter(
    (s) => !dismissedIds.has(s.id)
  );

  const visibleAutomations = AUTOMATION_SUGGESTIONS.filter(
    (s) => !dismissedIds.has(s.id)
  );

  return (
    <div className="workspace-suggestions-overlay" onClick={onClose}>
      <div className="workspace-suggestions-modal" onClick={(e) => e.stopPropagation()}>
        <div className="workspace-suggestions-header">
          <h2>Workspace Suggestions</h2>
          <button className="workspace-suggestions-close" onClick={onClose}>
            <X size={18} />
          </button>

          <div className="workspace-suggestions-tabs">
            <button
              className={`workspace-suggestions-tab ${activeTab === 'alerts' ? 'active' : ''}`}
              onClick={() => setActiveTab('alerts')}
            >
              <BellRing size={14} />
              Alerts
              <span className="workspace-suggestions-tab-count">{visibleAutomations.length}</span>
            </button>
            <button
              className={`workspace-suggestions-tab ${activeTab === 'insights' ? 'active' : ''}`}
              onClick={() => setActiveTab('insights')}
            >
              <Zap size={14} />
              Insights
              <span className="workspace-suggestions-tab-count">{visibleInsights.length}</span>
            </button>
            <button
              className={`workspace-suggestions-tab ${activeTab === 'reports' ? 'active' : ''}`}
              onClick={() => setActiveTab('reports')}
            >
              <FileText size={14} />
              Reports
              <span className="workspace-suggestions-tab-count">{SUGGESTED_REPORTS.length}</span>
            </button>
          </div>
        </div>

        <div className="workspace-suggestions-body">
          {/* Automated Alerts Tab */}
          {activeTab === 'alerts' && (
            visibleAutomations.length === 0 ? (
              <div className="workspace-suggestions-empty">
                <Inbox size={32} />
                <p>No alert suggestions</p>
                <span>New alerts will appear as more analysis patterns are detected.</span>
              </div>
            ) : (
              visibleAutomations.map((automation) => (
                <div key={automation.id} className="workspace-suggestion-card">
                  <div className="workspace-suggestion-card-icon">{automation.icon}</div>
                  <div className="workspace-suggestion-card-content">
                    <div className="workspace-suggestion-card-top">
                      <span className="workspace-suggestion-card-title">{automation.title}</span>
                    </div>
                    <p className="workspace-suggestion-card-desc">{automation.description}</p>
                    <span className="workspace-suggestion-card-source">
                      {automation.trigger} · {automation.basedOn}
                    </span>
                  </div>
                  <button
                    className="workspace-suggestion-dismiss-btn"
                    onClick={() => handleDismiss(automation.id)}
                  >
                    Dismiss
                  </button>
                </div>
              ))
            )
          )}

          {/* Workspace Insights Tab */}
          {activeTab === 'insights' && (
            visibleInsights.length === 0 ? (
              <div className="workspace-suggestions-empty">
                <Inbox size={32} />
                <p>No workspace insights</p>
                <span>New insights will appear as more workspace data is analyzed.</span>
              </div>
            ) : (
              visibleInsights.map((suggestion) => (
                <div key={suggestion.id} className="workspace-suggestion-card">
                  <div className="workspace-suggestion-card-icon">{suggestion.icon}</div>
                  <div className="workspace-suggestion-card-content">
                    <div className="workspace-suggestion-card-top">
                      <span className="workspace-suggestion-card-title">{suggestion.title}</span>
                      <span className={`workspace-suggestion-impact-badge impact-${suggestion.impact}`}>
                        {suggestion.impact}
                      </span>
                    </div>
                    <p className="workspace-suggestion-card-desc">{suggestion.description}</p>
                    <span className="workspace-suggestion-card-source">{suggestion.source}</span>
                  </div>
                  <button
                    className="workspace-suggestion-dismiss-btn"
                    onClick={() => handleDismiss(suggestion.id)}
                  >
                    Dismiss
                  </button>
                </div>
              ))
            )
          )}

          {/* Suggested Reports Tab */}
          {activeTab === 'reports' && (
            SUGGESTED_REPORTS.map((report) => {
              const isAdded = addedReportIds.has(report.id);
              return (
                <div key={report.id} className="workspace-suggestion-card">
                  <div className="workspace-suggestion-card-icon">{report.icon}</div>
                  <div className="workspace-suggestion-card-content">
                    <div className="workspace-suggestion-card-top">
                      <span className="workspace-suggestion-card-title">{report.name}</span>
                      <span className="workspace-suggestion-impact-badge impact-category">{report.category}</span>
                    </div>
                    <p className="workspace-suggestion-card-desc">{report.description}</p>
                    <span className="workspace-suggestion-card-source">
                      {report.complexity} · {report.outputFormats.join(' + ')}
                    </span>
                  </div>
                  <button
                    className={`workspace-suggestion-add-btn ${isAdded ? 'added' : ''}`}
                    onClick={() => handleAddReport(report.id)}
                    disabled={isAdded}
                  >
                    {isAdded ? 'Added' : 'Add'}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="workspace-suggestions-footer">
          <span className="workspace-suggestions-coming-soon-badge">Coming Soon</span>
          <button className="workspace-suggestions-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default WorkspaceSuggestionsModal;
